import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMusicPlayer } from '../context/MusicPlayerContext';
import { Button } from '../components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { 
  Home, Phone, MessageSquare, Calendar, CheckSquare, 
  FolderOpen, Menu, Search, User, Settings, LogOut, Lock, Users, Users2, Sparkles, Shield, ClipboardList, CreditCard, Brain, Bell, Voicemail, MapPin, Globe, Mail, ArrowLeft, MessageCircle, KeyRound, AtSign
} from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import { useStateScope } from '../context/StateScopeContext';
import { usePageHeaderState } from '../context/PageHeaderContext';
import PersistentMfaSetup from './PersistentMfaSetup';import PersistentPasswordRotation from './PersistentPasswordRotation';
import PersistentMfaRotation from './PersistentMfaRotation';
import PersistentRecoveryReveal from './PersistentRecoveryReveal';
import AdminOverrideReviewModal from './AdminOverrideReviewModal';
import ContactBottomSheet from './ContactBottomSheet';
import ReauthGate from './ReauthGate';
import useAutoLogout from '../hooks/useAutoLogout';

// Logo image URL — single black PNG with transparency. We use
// `dark:invert` to flip black→white in dark mode so the same file
// covers both themes. Cache-busted with ?v= so deployed clients
// pick up the new asset immediately instead of serving the old
// customer-assets URL from a stale service-worker cache.
const LOGO_HEADER = "/orb-logo-header.png?v=2026-05-17";
// Backwards-compat exports so any other component still importing
// these names continues to work without a sweeping rename.
const LOGO_DARK = LOGO_HEADER;
const LOGO_LIGHT = LOGO_HEADER;

const navItems = [
  { path: '/', label: 'Home', icon: Home, requiresOnboarding: false, featureId: 'home_entire' },
  { path: '/calls', label: 'Calls', icon: Phone, requiresOnboarding: true, featureId: 'calls_entire' },
  { path: '/chat', label: 'Chat', icon: MessageSquare, requiresOnboarding: true, featureId: 'chat_entire' },
  { path: '/email', label: 'Email', icon: Mail, requiresOnboarding: true, featureId: null },
  { path: '/calendar', label: 'Calendar', icon: Calendar, requiresOnboarding: true, featureId: 'calendar_entire' },
  { path: '/tasks', label: 'Tasks', icon: CheckSquare, requiresOnboarding: true, featureId: 'tasks_entire' },
];

const Layout = ({ children }) => {
  // Per-role inactivity auto-logout (skips during active calls).
  useAutoLogout();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isFeatureEnabled, api } = useAuth();
  const { theme } = useTheme();
  const { brandLabel, logoUrl: scopeLogoUrl, isRestricted } = useStateScope();
  const { currentSong } = useMusicPlayer();
  const { config: pageHeader } = usePageHeaderState();
  const [chatInputFocused, setChatInputFocused] = useState(false);
  // iter140 — desktop hamburger now renders as a left-side Sheet drawer.
  const [menuOpen, setMenuOpen] = useState(false);
  // iter161.3 — mobile hamburger ALSO uses a Sheet drawer (was a tiny
  // DropdownMenu) so the slide-out animation + full-height vertical
  // scroll list matches the desktop pattern exactly.
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // iter164 — When the page is loaded as an onboarding popup iframe
  // (URL contains `?embedded=1`), strip ALL chrome (top header,
  // bottom nav, sidebar drawer). Render only the children so the
  // page slots cleanly inside the popup.
  //
  // NOTE: We compute the flag here but do NOT early-return — that
  // would violate Rules of Hooks because many useState/useEffect
  // calls follow this block. The actual chrome-less branch lives
  // right above the main `return (` JSX further down.
  const isEmbedded = (typeof window !== 'undefined' && window.location.search.includes('embedded=1'));

  // ORB embed detection — when Uplift runs inside ORB's <iframe>
  // (via /u/:universe_id/signin), the host frame is small and the
  // user expects mobile-style ergonomics. On desktop widths we
  // suppress the top center nav and surface the bottom nav instead
  // so Home / Chat / Calendar / etc. stay one tap away at the
  // bottom of the embed. Detection runs once on mount because
  // window.self vs window.top can't change during a page lifetime.
  //
  // Default `false` so any browser-security throw FAILS CLOSED —
  // standalone visits to orbuplift.com NEVER get the embed UI.
  // Only true iframe loads (window.self !== window.top, verified
  // without throwing) flip the flag on.
  const [isOrbEmbed] = useState(() => {
    try { return window.self !== window.top; } catch { return false; }
  });

  // Mirror the detection to a `<html>` data attribute so the
  // matching CSS (App.css → html[data-orb-embed="true"]) can force
  // --app-bottom-h to 4rem on desktop landscape and keep main's
  // padding-bottom from collapsing under the now-visible bottom nav.
  useEffect(() => {
    const root = document.documentElement;
    if (isOrbEmbed) root.setAttribute('data-orb-embed', 'true');
    else root.removeAttribute('data-orb-embed');
    return () => root.removeAttribute('data-orb-embed');
  }, [isOrbEmbed]);
  
  // Check if mini player is visible
  const hasMiniPlayer = !!currentSong;

  // Listen for chat input focus events — match any chat-style input
  // (chat-input, team-chat-input, team-message-input, summarize-input, …)
  useEffect(() => {
    const isChatInputEl = (el) => {
      const t = el?.dataset?.testid || '';
      return (
        t === 'chat-input' ||
        t.endsWith('-chat-input') ||
        t === 'team-message-input' ||
        t === 'summarize-input' ||
        t === 'new-team-message'
      );
    };
    const handleFocusIn = (e) => {
      if (isChatInputEl(e.target)) setChatInputFocused(true);
    };
    const handleFocusOut = (e) => {
      if (isChatInputEl(e.target)) setChatInputFocused(false);
    };
    
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  // Get logo based on theme
  // Prefer the state-branding logo when a restricted state has one uploaded.
  const logoUrl = scopeLogoUrl || (theme === 'dark' ? LOGO_DARK : LOGO_LIGHT);

  // `hasAdmin(featureId)` — grants access to admin-only nav items if:
  //   • user is a full admin (role === 'admin'), OR
  //   • user has been granted that specific admin-feature toggle by an
  //     admin via the Create User / Edit User dialog.
  const hasAdmin = React.useCallback((featureId) => {
    if (user?.role === 'admin') return true;
    const toggles = user?.feature_toggles || {};
    return toggles[featureId] === true;
  }, [user]);

  // Unread notification count (polled every 30s) — pulse + vibrate + browser
  // notification when the count rises (so iOS / desktop users feel a ping).
  const [unreadCount, setUnreadCount] = useState(0);
  const [pulse, setPulse] = useState(false);
  const lastCountRef = React.useRef(0);
  const lastTitleRef = React.useRef(null);

  // ---------- iOS keyboard handling ----------
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // GUARD: this entire scroll-lock recipe exists ONLY to work around
    // iOS Safari's hardcoded "scroll the viewport up when an input near
    // the bottom is focused" behavior. On every other platform (macOS
    // Chrome / Safari, Windows, Android Chrome) running it is actively
    // harmful — it pins `body { position: fixed; top: -<scrollY>px }`
    // which yanks the entire page off-screen the moment you focus an
    // input on a long-scrolled page (the "page turns gray when I click
    // the Add Exemption display-name field" bug). Limit it to real iOS.
    const ua = window.navigator.userAgent || '';
    const isIOS =
      /iPad|iPhone|iPod/.test(ua) ||
      // iPadOS 13+ identifies as Mac with touch support — detect it.
      (ua.includes('Mac') && 'ontouchend' in document);
    if (!isIOS) return;

    // PART 1: Radix-style scroll lock fired on text-input focus.
    //
    // Why this exact recipe: iOS Safari has a hardcoded behavior of
    // scrolling the entire viewport upward whenever a text input is
    // focused near the bottom edge — that's what was pushing the
    // global header off the top. Setting `body { overflow: hidden }`
    // alone is NOT enough on iOS. The bulletproof pattern (the same
    // one Radix UI uses inside `<Dialog>`, hence why the Calendar
    // Create Appointment popup stays put) is:
    //   1. Capture current scrollY
    //   2. body.position = 'fixed' + body.top = `-${scrollY}px`
    //   3. body.width = 100% so reflow doesn't shrink content
    //   4. On unlock, restore the scroll position with window.scrollTo
    // We trigger this synchronously off the `focusin` event so the
    // lock is in place BEFORE iOS gets a chance to scroll.
    const isEditable = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      if (tag === 'INPUT') {
        const t = (el.type || 'text').toLowerCase();
        return ['text','search','email','url','tel','password','number','date','time','datetime-local','month','week'].includes(t);
      }
      return tag === 'TEXTAREA' || el.isContentEditable;
    };
    let scrollLocked = false;
    let savedY = 0;
    const lockScroll = () => {
      if (scrollLocked) return;
      scrollLocked = true;
      savedY = window.scrollY || window.pageYOffset || 0;
      const body = document.body;
      body.style.position = 'fixed';
      body.style.top = `-${savedY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      document.documentElement.setAttribute('data-kb-locked', '');
    };
    const unlockScroll = () => {
      if (!scrollLocked) return;
      scrollLocked = false;
      const body = document.body;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      document.documentElement.removeAttribute('data-kb-locked');
      window.scrollTo(0, savedY);
    };
    const onFocusIn = (e) => {
      // Skip the scroll-lock when focus enters a Radix Dialog (or any
      // open modal portal). Radix renders its content via a portal and
      // positions it with `position: fixed; top: 50%`. If we set
      // `body { position: fixed; top: -<scrollY>px }` while the dialog
      // is open, the body becomes the containing block for the
      // dialog's fixed positioning and pushes the dialog content
      // entirely off-screen — leaving only the dark overlay visible
      // (the "page goes black when I click the input inside the
      // exemption Edit dialog" bug reported on iter137). The dialog
      // has its own scroll-lock so this layer is redundant inside it.
      if (e.target && typeof e.target.closest === 'function') {
        if (e.target.closest('[role="dialog"]') || e.target.closest('[data-radix-popper-content-wrapper]')) {
          return;
        }
      }
      if (isEditable(e.target)) lockScroll();
    };
    const onFocusOut = () => {
      // Defer so we don't unlock if focus is moving to another input
      // (e.g., tab between fields in a form).
      setTimeout(() => {
        if (!isEditable(document.activeElement)) unlockScroll();
      }, 0);
    };
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);

    // iter163 — PWA-resume safety net.
    // Symptom (from production): "user scrolls down on profile, screen
    // keeps snapping back to top, continuously. Closing + reopening
    // the PWA fixed it." This is the classic iOS PWA suspension bug:
    // 1. User focuses an input → lockScroll() fires → body becomes
    //    `position: fixed; top: -<savedY>px`.
    // 2. iOS suspends the PWA process while the user switches apps
    //    (notification, message, etc.).
    // 3. PWA resumes. The body is STILL `position: fixed`. The
    //    focusout listener never fired because JS was paused. The
    //    user-facing effect: every scroll gesture is clamped because
    //    body is locked at top=-<oldY>; iOS re-pins it on every
    //    paint, producing the "jumps back up" behavior.
    // 4. Closing + reopening the PWA reinitialises the document and
    //    the body styles are blanked → bug "disappears."
    //
    // Fix: on `pageshow` and `visibilitychange→visible`, if our
    // internal `scrollLocked` flag is true BUT no editable element
    // is focused, force-release the lock. Equally, even when our
    // flag thinks we're not locked, if the body somehow has
    // position:fixed left on it (stale snapshot from before-suspend),
    // forcibly clear it so the page is scrollable again.
    const forceReleaseStaleLock = () => {
      const body = document.body;
      const activeEditable = isEditable(document.activeElement);
      if (activeEditable) return; // a real input is focused; leave as-is
      // Case 1: we still THINK we're locked but no input is focused.
      if (scrollLocked) {
        unlockScroll();
        return;
      }
      // Case 2: our flag was reset by some other code path but body
      // styles linger from before suspension. Wipe them defensively.
      if (body.style.position === 'fixed') {
        body.style.position = '';
        body.style.top = '';
        body.style.left = '';
        body.style.right = '';
        body.style.width = '';
        body.style.overflow = '';
        document.documentElement.removeAttribute('data-kb-locked');
      }
    };
    const onPageShow = () => forceReleaseStaleLock();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') forceReleaseStaleLock();
    };
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibilityChange);

    // PART 2: visualViewport-based --kb-offset for content that needs
    // to know the keyboard's height (ChatPage lifts its input above
    // the keyboard via this var).
    let vvCleanup = () => {};
    if (window.visualViewport) {
      const vv = window.visualViewport;
      const apply = () => {
        const offset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
        document.documentElement.style.setProperty('--kb-offset', `${offset}px`);
        document.documentElement.classList.toggle('kb-open', offset > 60);
      };
      apply();
      vv.addEventListener('resize', apply);
      vv.addEventListener('scroll', apply);
      vvCleanup = () => {
        vv.removeEventListener('resize', apply);
        vv.removeEventListener('scroll', apply);
      };
    }

    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      vvCleanup();
      // Make sure we don't leave the body locked on unmount.
      unlockScroll();
    };
  }, []);

  // ---------- Service Worker — iOS PWA push + background notifications ----------
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    navigator.serviceWorker
      .register('/sw.js')
      .catch((e) => console.warn('SW register failed', e));
  }, []);

  // ---------- Real Web Push subscription (VAPID) ----------
  // Asks the browser for a real PushSubscription and persists it on the backend
  // so notifications fire even when the PWA / Safari tab is fully closed.
  useEffect(() => {
    if (!user || !api) return;
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    const urlBase64ToUint8Array = (b64) => {
      const padding = '='.repeat((4 - (b64.length % 4)) % 4);
      const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
      const raw = window.atob(base64);
      const out = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
      return out;
    };

    let cancelled = false;
    (async () => {
      try {
        // 1) Permission (prompt only if needed; user already consented for in-page notifs)
        if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
          try { await Notification.requestPermission(); } catch (e) { /* ignore */ }
        }
        if (Notification.permission !== 'granted') return;

        // 2) Get the VAPID public key from the backend
        const keyRes = await api.get('/push/vapid-public-key');
        const vapidKey = keyRes?.data?.public_key;
        if (!vapidKey) return;

        const reg = await navigator.serviceWorker.ready;
        if (cancelled) return;

        // 3) Reuse an existing subscription OR create a new one
        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidKey),
          });
        }
        if (cancelled || !sub) return;

        // 4) POST it to the backend (idempotent — endpoint is the unique key)
        await api.post('/push/subscribe', {
          subscription: sub.toJSON(),
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        });
      } catch (e) {
        // Non-fatal — push just won't work for this device.
        console.warn('[Push] subscribe failed:', e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, [user, api]);

  useEffect(() => {
    if (!user || !api) return;
    let alive = true;

    // Ask for notification permission on the FIRST user click after login.
    // iOS Safari requires the prompt to come from a user gesture or it is
    // silently ignored — calling Notification.requestPermission() directly
    // inside useEffect on mount does NOT count. Listening for one pointer
    // event globally and triggering the prompt there gets us a real
    // permission dialog on iPad PWA + iPhone PWA.
    if (typeof window !== 'undefined' && 'Notification' in window) {
      const askOnGesture = () => {
        try {
          if (Notification.permission === 'default') {
            Notification.requestPermission().catch(() => {});
          }
        } catch (e) { /* ignore */ }
        window.removeEventListener('pointerdown', askOnGesture, true);
        window.removeEventListener('keydown', askOnGesture, true);
      };
      try {
        if (Notification.permission === 'default') {
          window.addEventListener('pointerdown', askOnGesture, true);
          window.addEventListener('keydown', askOnGesture, true);
        }
      } catch (e) { /* ignore */ }
    }

    const fetchCount = async () => {
      try {
        const res = await api.get('/app-notifications');
        if (!alive) return;
        const items = res.data.items || [];
        const unread = items.filter((i) => !i.is_read);
        setUnreadCount(unread.length);
        if (unread.length > lastCountRef.current) {
          // New unread arrived since last poll: pulse + vibrate + notify
          setPulse(true);
          setTimeout(() => setPulse(false), 4000);
          try { navigator.vibrate?.([120, 60, 120]); } catch (e) {}
          const newest = unread[0];
          if (newest && newest.title && newest.id !== lastTitleRef.current) {
            lastTitleRef.current = newest.id;
            const showOpts = {
              body: newest.title + (newest.message ? `\n${newest.message}` : ''),
              tag: newest.id,
              icon: '/icons/icon-192x192.png',
              badge: '/icons/icon-192x192.png',
              vibrate: [200, 100, 200],
              data: { url: newest.link || '/notifications' },
            };
            try {
              // Prefer the Service Worker so the notification persists even if
              // the user backgrounds the tab/app on iOS.
              if (navigator.serviceWorker?.controller) {
                navigator.serviceWorker.controller.postMessage({
                  type: 'SHOW_NOTIFICATION',
                  title: 'The Uplift',
                  options: showOpts,
                });
              } else if (navigator.serviceWorker?.ready) {
                navigator.serviceWorker.ready.then((reg) => {
                  reg.showNotification('The Uplift', showOpts);
                }).catch(() => {});
              } else if ('Notification' in window && Notification.permission === 'granted') {
                const n = new Notification('The Uplift', showOpts);
                n.onclick = () => {
                  window.focus();
                  if (newest.link) navigate(newest.link);
                  n.close();
                };
              }
            } catch (e) { /* ignore */ }
          }
        }
        lastCountRef.current = unread.length;
      } catch (e) { /* silent */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    // Listen for explicit "notifications changed" pings (mark-read,
    // mark-all-read) so the bell badge updates instantly instead of
    // waiting for the 30s poll cycle. The event can carry an
    // optional `count` so the badge snaps to the known value even
    // before the network round-trip completes.
    const onChanged = (evt) => {
      if (evt?.detail && typeof evt.detail.count === 'number') {
        setUnreadCount(evt.detail.count);
        lastCountRef.current = evt.detail.count;
      }
      fetchCount();
    };
    window.addEventListener('app-notifications-changed', onChanged);
    return () => {
      alive = false;
      clearInterval(t);
      window.removeEventListener('app-notifications-changed', onChanged);
    };
  }, [user, api, navigate]);

  // Mirror the bell badge onto the OS-level home-screen icon via
  // the Web App Badging API. Supported by iOS Safari 16.4+ (when
  // installed via "Add to Home Screen") and Chrome on Android for
  // installed PWAs. No-op on browsers that don't support it.
  //
  // Also runs on visibilitychange + focus so the icon is refreshed
  // whenever the user comes back to the app after switching away
  // (covers the iPad PWA case where the badge may have been cleared
  // by iOS when the user opened the app, but new notifications have
  // arrived in the background).
  useEffect(() => {
    const apply = () => {
      try {
        if (typeof navigator === 'undefined') return;
        if (typeof navigator.setAppBadge !== 'function') return;
        if (unreadCount > 0) {
          navigator.setAppBadge(unreadCount).catch(() => {});
        } else {
          // Clear via setAppBadge(0) for broader compatibility (iPad +
          // some Chromium versions don't expose clearAppBadge separately).
          navigator.setAppBadge(0).catch(() => {
            if (typeof navigator.clearAppBadge === 'function') {
              navigator.clearAppBadge().catch(() => {});
            }
          });
        }
      } catch (_e) {/* unsupported — silently ignore */}
    };
    apply();
    window.addEventListener('visibilitychange', apply);
    window.addEventListener('focus', apply);
    return () => {
      window.removeEventListener('visibilitychange', apply);
      window.removeEventListener('focus', apply);
    };
  }, [unreadCount]);

  // Check if user needs onboarding
  const needsOnboarding = user?.role !== 'admin' && !user?.is_onboarded;

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleLockedTabClick = (e, item) => {
    if (needsOnboarding && item.requiresOnboarding) {
      e.preventDefault();
      toast.info('Complete all onboarding steps on the Home page to unlock this feature');
    }
  };

  // iter164 fix — embedded onboarding iframe: render children with
  // no chrome. Placed AFTER all hooks so Rules of Hooks aren't
  // violated (the early-return version was breaking the compile).
  if (isEmbedded) {
    return <div className="min-h-screen bg-white dark:bg-[#0a0a0a]">{children}</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Persistent MFA Setup — non-dismissible. Renders on top of
          everything (z-[10000]) whenever the user's auth context
          carries `mfa_setup_required`. Survives logout/relogin because
          the flag is server-side. See PersistentMfaSetup.js. */}
      <PersistentMfaSetup />
      <PersistentPasswordRotation />
      <PersistentMfaRotation />
      <PersistentRecoveryReveal />
      <AdminOverrideReviewModal />
      <ReauthGate />
      <ContactBottomSheet />

      {/* Desktop Header — `pt: env(safe-area-inset-top)` protects the
          iOS status bar (battery/wifi indicators) when the app runs
          standalone on iPad/Safari PWA so the header doesn't merge
          with the OS chrome. */}
      <header
        className="hidden md:block sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="w-full grid grid-cols-[1fr_auto_1fr] h-16 items-center px-6 gap-4">
          {/* Left: Orb logo (always visible on desktop, acts as the menu
              trigger) + dynamic page title text. The title text swaps
              between the brand label and the current page's title so the
              user always knows where they are AND can always open the
              menu from any page. */}
          <div className="col-start-1 flex items-center gap-2 min-w-0 justify-self-start" data-testid="desktop-page-header">
            {/* Optional back arrow — shown for pages that pass leftSlot/back,
                but the logo+menu trigger stays in place next to it. */}
            {pageHeader?.leftSlot ? (
              <div className="flex-shrink-0">{pageHeader.leftSlot}</div>
            ) : pageHeader?.back ? (
              <button
                onClick={() => {
                  if (typeof pageHeader.back === 'function') pageHeader.back();
                  else navigate(pageHeader.back);
                }}
                className="w-9 h-9 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center flex-shrink-0"
                aria-label="Back"
                data-testid="desktop-page-header-back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            ) : null}
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-md px-1 py-1 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] min-w-0"
                  data-testid="desktop-hamburger-btn"
                  aria-label="Open menu"
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                    <img src={logoUrl} alt="The Uplift" className="w-full h-full object-contain dark:invert" />
                  </div>
                  <span
                    className="font-bold text-lg truncate"
                    style={!isOrbEmbed ? { transform: 'translateY(1px)' } : undefined}
                    data-testid={pageHeader?.title ? 'desktop-page-header-title' : 'desktop-brand-label'}
                  >
                    {pageHeader?.title || brandLabel}
                  </span>
                </button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="w-72 sm:max-w-xs p-0 flex flex-col"
                style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
                data-testid="desktop-side-drawer"
              >
                {/* Sheet renders its own close X in the top-right. Item list scrolls. */}
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <p className="font-medium truncate">{user?.full_name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{user?.title || user?.role}</p>
                  {needsOnboarding && (
                    <span className="text-xs text-yellow-600">Onboarding pending</span>
                  )}
                </div>
                <nav className="flex-1 overflow-y-auto py-2">
                  {(() => {
                    // Compact helper so every item stays consistent without
                    // dragging DropdownMenuItem-specific keyboard logic in
                    // (Sheet items are plain buttons — Radix DropdownMenu
                    // expects roving focus inside its own context, which
                    // doesn't apply here).
                    const DrawerItem = ({ icon: Icon, label, to, testid }) => (
                      <button
                        onClick={() => { setMenuOpen(false); navigate(to); }}
                        data-testid={testid}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors text-left"
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{label}</span>
                      </button>
                    );
                    return (
                      <>
                        {/* iter160 — Menu order, user-first:
                            (1) Personal items the user touches daily
                            (2) People they connect with
                            (3) Discovery / fun
                            (4) Admin tools (gated)
                            (5) Settings — sits just before the divider
                                so it acts as the user's last stop
                                before Feedback. */}

                        {/* — Personal — */}
                        <DrawerItem icon={CreditCard} label="Business Card" to="/business-card" testid="menu-business-card" />
                        <DrawerItem icon={FolderOpen} label="Memos" to="/memos" testid="menu-memos" />
                        <DrawerItem icon={Voicemail} label="Voicemail" to="/voicemail" testid="menu-voicemail" />
                        <DrawerItem icon={FolderOpen} label="Resources" to="/resources" testid="menu-resources" />

                        {/* — People — */}
                        {(user?.role === 'admin' || user?.role === 'supervisor' || user?.role === 'lead') && isFeatureEnabled('my_team_entire') && (
                          <DrawerItem icon={Users} label="My Team" to="/my-team" testid="menu-my-team" />
                        )}
                        {isFeatureEnabled('my_peers_entire') && (
                          <DrawerItem icon={Users2} label="My Peers" to="/my-peers" testid="menu-my-peers" />
                        )}
                        {isFeatureEnabled('search_users_entire') && (
                          <DrawerItem icon={Search} label="Search Users" to="/search" testid="menu-search" />
                        )}

                        {/* — Discovery — */}
                        {isFeatureEnabled('orb_characters_entire') && !isRestricted && (
                          <DrawerItem icon={Sparkles} label="Characters" to="/orb-characters" testid="menu-orb-characters" />
                        )}

                        {/* — Customization (restricted supervisor only) — */}
                        {isRestricted && user?.role === 'supervisor' && (
                          <DrawerItem icon={Sparkles} label="Customization" to="/customization" testid="menu-customization-desktop" />
                        )}

                        {/* — Admin — */}
                        {hasAdmin('admin_onboarding') && (
                          <DrawerItem icon={ClipboardList} label="Onboarding" to="/admin/onboarding" testid="menu-onboarding-admin" />
                        )}
                        {hasAdmin('admin_user_administration') && (
                          <DrawerItem icon={Shield} label="User Administration" to="/admin/users" testid="menu-user-admin" />
                        )}
                        {hasAdmin('admin_user_communications') && (
                          <DrawerItem icon={MessageSquare} label="User Communications" to="/admin/user-communications" testid="menu-user-communications" />
                        )}
                        {user?.role === 'admin' && (
                          <DrawerItem icon={Mail} label="Email Verifications" to="/admin/email-verifications" testid="menu-email-verifications" />
                        )}
                        {user?.role === 'admin' && (
                          <DrawerItem icon={AtSign} label="ORB Emails" to="/admin/orb-emails" testid="menu-orb-emails" />
                        )}
                        {hasAdmin('admin_calling') && (
                          <DrawerItem icon={Phone} label="Calling" to="/admin/calling" testid="menu-calling-admin" />
                        )}
                        {hasAdmin('admin_ai_training') && (
                          <DrawerItem icon={Brain} label="AI Training" to="/admin/ai-training" testid="menu-ai-training" />
                        )}
                        {hasAdmin('admin_orb_universes') && (
                          <DrawerItem icon={Globe} label="Universe" to="/admin/orb-universes" testid="menu-orb-universes" />
                        )}
                        {hasAdmin('admin_statewide_details') && (
                          <DrawerItem icon={MapPin} label="Statewide Details" to="/admin/statewide-details" testid="menu-statewide-details" />
                        )}
                        {hasAdmin('admin_restricted_states') && (
                          <DrawerItem icon={Shield} label="Restricted States" to="/admin/restricted-states" testid="menu-restricted-states" />
                        )}
                        {hasAdmin('admin_security_requirements') && (
                          <>
                            <DrawerItem icon={Lock} label="Security Requirements" to="/admin/security-requirements" testid="menu-security-requirements" />
                            <DrawerItem icon={KeyRound} label="LLM Routing" to="/admin/llm-slots" testid="menu-llm-slots" />
                          </>
                        )}

                        {/* — Settings — last stop before divider — */}
                        <DrawerItem icon={Settings} label="Settings" to="/settings" testid="menu-settings" />

                        <div className="my-2 border-t border-gray-200 dark:border-gray-700" />
                        <DrawerItem icon={MessageCircle} label="Feedback" to="/feedback" testid="menu-feedback-desktop" />
                        {/* Sign Out lives at the bottom of /profile (iter140). */}
                      </>
                    );
                  })()}
                </nav>
              </SheetContent>
            </Sheet>
          </div>

          {/* Center: Main Nav (Home/Calls/Chat/Email/Calendar/Tasks).
              On iPad in PORTRAIT orientation the nav moves to the
              bottom of the screen (see the bottom-nav block below +
              the matching @media rule in App.css). On landscape
              desktop/iPad it stays in the top header.
              ORB embed: hidden everywhere — the bottom nav takes
              over so the embed feels like the mobile app. */}
          <nav
            className={`flex items-center justify-center gap-1 ${isOrbEmbed ? 'hidden' : ''}`}
            data-layout-top-nav
          >
            {navItems.filter(item => !item.featureId || isFeatureEnabled(item.featureId)).map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const isLocked = needsOnboarding && item.requiresOnboarding;

              return (
                <Link
                  key={item.path}
                  to={isLocked ? '#' : item.path}
                  onClick={(e) => handleLockedTabClick(e, item)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <Button
                    variant={isActive ? 'secondary' : 'ghost'}
                    size="sm"
                    className={`gap-2 relative ${isActive ? 'bg-primary/10 text-primary' : ''} ${isLocked ? 'opacity-50' : ''}`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                    {isLocked && <Lock className="w-3 h-3 absolute -top-1 -right-1 text-muted-foreground" />}
                  </Button>
                </Link>
              );
            })}
          </nav>

          {/* Right: page rightSlot + notifications + profile avatar.
              `col-start-3` forces this group into the third grid column
              even when the center nav is hidden via `display: none` in
              iPad portrait — without it, CSS Grid auto-placement would
              flow this div into the empty column 2, leaving the icons
              floating in the middle of the header. */}
          <div className="col-start-3 flex items-center gap-1 justify-self-end">
            {pageHeader?.rightSlot}
            <button
              onClick={() => navigate('/notifications')}
              className="relative w-10 h-10 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center"
              data-testid="desktop-notifications-btn"
              aria-label="Notifications"
            >
              <Bell className={`w-5 h-5 ${pulse ? 'animate-bell-shake' : ''}`} />
              {unreadCount > 0 && (
                <>
                  {pulse && (
                    <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 rounded-full bg-red-500 opacity-75 animate-ping" />
                  )}
                  <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                </>
              )}
            </button>
            <Button
              variant="ghost"
              size="icon"
              className="!rounded-md h-10 w-8 p-0"
              onClick={() => navigate('/profile')}
              data-testid="user-menu-btn"
              aria-label="View profile"
              title={user?.full_name || 'Profile'}
            >
              {/* iter175z — `key` swap forces Radix Avatar to fully remount when
                  the user uploads then deletes their photo. Without it, Radix's
                  internal `imageLoadingStatus` context keeps reporting 'loaded'
                  from the stale AvatarImage, hiding the fallback even after the
                  AvatarImage child is conditionally unmounted → user saw an
                  empty black avatar instead of their initials. */}
              <Avatar key={user?.profile_picture ? 'photo' : 'no-photo'} className="!rounded-md h-9 w-7 overflow-hidden">
                {user?.profile_picture && (
                  <AvatarImage
                    src={user.profile_picture}
                    alt={user?.full_name || 'Profile'}
                    className="object-cover object-top"
                    style={{ aspectRatio: 'auto' }}
                    data-testid="desktop-avatar-image"
                  />
                )}
                <AvatarFallback className="bg-primary/10 text-primary text-sm !rounded-md">
                  {getInitials(user?.full_name)}
                </AvatarFallback>
              </Avatar>
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Header — mirrors the desktop pattern exactly:
            LEFT  = logo + page/brand title; tapping ANYWHERE in this
                    region opens the slide-down menu (same as desktop's
                    SheetTrigger on the logo lockup).
            RIGHT = notifications bell + user avatar that navigates
                    straight to /profile when tapped (same as desktop's
                    `user-menu-btn` Avatar in the top-right).
          The "View Profile" entry has been removed from the menu list
          itself since the avatar already covers that destination. */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 border-b bg-white dark:bg-[#141414]" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="flex items-center justify-between h-14 px-4">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <button
              className="flex items-center gap-2 min-w-0 rounded-md px-1 py-1 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] -ml-1"
              data-testid="mobile-menu-trigger"
              aria-label="Open menu"
            >
              {/* iter161.2 — Every mobile page (nav-bar AND hamburger
                  pages) now shows the ORB logo + a single title
                  string at the same font size. Hamburger pages
                  previously hid the logo and rendered the title at
                  text-2xl which made them look totally different
                  from nav-bar pages. The optional back-arrow / leftSlot
                  still appears BEFORE the logo, but the logo +
                  text are constant across every page. */}
              {pageHeader?.leftSlot ? (
                <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>{pageHeader.leftSlot}</div>
              ) : pageHeader?.back ? (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    if (typeof pageHeader.back === 'function') pageHeader.back();
                    else navigate(pageHeader.back);
                  }}
                  className="w-9 h-9 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center flex-shrink-0"
                  aria-label="Back"
                  data-testid="mobile-page-header-back"
                >
                  <ArrowLeft className="w-5 h-5" />
                </span>
              ) : null}
              <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0" data-testid="mobile-logo-link">
                <img src={logoUrl} alt="The Uplift" className="w-full h-full object-contain dark:invert" />
              </div>
              <span
                className="font-bold truncate"
                style={{ transform: isOrbEmbed ? 'translate(-4px, 2px)' : 'translate(-4px, 3px)' }}
                data-testid={pageHeader?.title ? 'mobile-page-header-title' : 'mobile-brand-label'}
              >
                {pageHeader?.title || brandLabel}
              </span>
            </button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="w-72 sm:max-w-xs p-0 flex flex-col"
            style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
            data-testid="mobile-side-drawer"
          >
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <p className="font-medium truncate">{user?.full_name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.title || user?.role}</p>
              {needsOnboarding && (
                <span className="text-xs text-yellow-600">Onboarding pending</span>
              )}
            </div>
            <nav className="flex-1 overflow-y-auto py-2">
              {(() => {
                // Same DrawerItem helper as the desktop side drawer so
                // both surfaces share identical visual & interaction
                // patterns (full-width row, icon left, label right,
                // muted-hover background, sheet auto-closes on tap).
                const DrawerItem = ({ icon: Icon, label, to, testid }) => (
                  <button
                    onClick={() => { setMobileMenuOpen(false); navigate(to); }}
                    data-testid={testid}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors text-left"
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{label}</span>
                  </button>
                );
                return (
                  <>
                    {/* iter160 — Mirror of desktop ordering. See desktop
                        drawer for rationale. */}

                    {/* — Personal — */}
                    <DrawerItem icon={CreditCard} label="Business Card" to="/business-card" testid="mobile-menu-business-card" />
                    <DrawerItem icon={FolderOpen} label="Memos" to="/memos" testid="mobile-menu-memos" />
                    <DrawerItem icon={Voicemail} label="Voicemail" to="/voicemail" testid="mobile-menu-voicemail" />
                    <DrawerItem icon={FolderOpen} label="Resources" to="/resources" testid="mobile-menu-resources" />

                    {/* — People — */}
                    {(user?.role === 'admin' || user?.role === 'supervisor' || user?.role === 'lead') && isFeatureEnabled('my_team_entire') && (
                      <DrawerItem icon={Users} label="My Team" to="/my-team" testid="mobile-menu-my-team" />
                    )}
                    {isFeatureEnabled('my_peers_entire') && (
                      <DrawerItem icon={Users2} label="My Peers" to="/my-peers" testid="mobile-menu-my-peers" />
                    )}
                    {isFeatureEnabled('search_users_entire') && (
                      <DrawerItem icon={Search} label="Search Users" to="/search" testid="mobile-menu-search" />
                    )}

                    {/* — Discovery — */}
                    {isFeatureEnabled('orb_characters_entire') && !isRestricted && (
                      <DrawerItem icon={Sparkles} label="Characters" to="/orb-characters" testid="mobile-menu-orb-characters" />
                    )}

                    {isRestricted && user?.role === 'supervisor' && (
                      <DrawerItem icon={Sparkles} label="Customization" to="/customization" testid="mobile-menu-customization" />
                    )}

                    {/* — Admin — */}
                    {hasAdmin('admin_onboarding') && (
                      <DrawerItem icon={ClipboardList} label="Onboarding" to="/admin/onboarding" testid="mobile-menu-onboarding-admin" />
                    )}
                    {hasAdmin('admin_user_administration') && (
                      <DrawerItem icon={Shield} label="User Administration" to="/admin/users" testid="mobile-menu-user-admin" />
                    )}
                    {hasAdmin('admin_user_communications') && (
                      <DrawerItem icon={MessageSquare} label="User Communications" to="/admin/user-communications" testid="mobile-menu-user-communications" />
                    )}
                    {user?.role === 'admin' && (
                      <DrawerItem icon={Mail} label="Email Verifications" to="/admin/email-verifications" testid="mobile-menu-email-verifications" />
                    )}
                    {user?.role === 'admin' && (
                      <DrawerItem icon={AtSign} label="ORB Emails" to="/admin/orb-emails" testid="mobile-menu-orb-emails" />
                    )}
                    {hasAdmin('admin_calling') && (
                      <DrawerItem icon={Phone} label="Calling" to="/admin/calling" testid="mobile-menu-calling-admin" />
                    )}
                    {hasAdmin('admin_ai_training') && (
                      <DrawerItem icon={Brain} label="AI Training" to="/admin/ai-training" testid="mobile-menu-ai-training" />
                    )}
                    {hasAdmin('admin_orb_universes') && (
                      <DrawerItem icon={Globe} label="Universe" to="/admin/orb-universes" testid="mobile-menu-orb-universes" />
                    )}
                    {hasAdmin('admin_statewide_details') && (
                      <DrawerItem icon={MapPin} label="Statewide Details" to="/admin/statewide-details" testid="mobile-menu-statewide-details" />
                    )}
                    {hasAdmin('admin_restricted_states') && (
                      <DrawerItem icon={Shield} label="Restricted States" to="/admin/restricted-states" testid="mobile-menu-restricted-states" />
                    )}
                    {hasAdmin('admin_security_requirements') && (
                      <>
                        <DrawerItem icon={Lock} label="Security Requirements" to="/admin/security-requirements" testid="mobile-menu-security-requirements" />
                        <DrawerItem icon={KeyRound} label="LLM Routing" to="/admin/llm-slots" testid="mobile-menu-llm-slots" />
                      </>
                    )}

                    {/* — Settings — last stop before divider — */}
                    <DrawerItem icon={Settings} label="Settings" to="/settings" testid="mobile-menu-settings" />

                    <div className="my-2 border-t border-gray-200 dark:border-gray-700" />
                    <DrawerItem icon={MessageCircle} label="Feedback" to="/feedback" testid="mobile-menu-feedback" />
                    {/* Sign Out lives at the bottom of /profile (iter140). */}
                  </>
                );
              })()}
            </nav>
          </SheetContent>
        </Sheet>

          <div className="flex items-center gap-1">
            {pageHeader?.rightSlot}
            <button
              onClick={() => navigate('/notifications')}
              className="relative w-10 h-10 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center"
              data-testid="mobile-notifications-btn"
              aria-label="Notifications"
            >
              <Bell className={`w-5 h-5 ${pulse ? 'animate-bell-shake' : ''}`} />
              {unreadCount > 0 && (
                <>
                  {pulse && (
                    <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 rounded-full bg-red-500 opacity-75 animate-ping" />
                  )}
                  <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                </>
              )}
            </button>
            {/* User avatar — matches desktop's user-menu-btn pattern.
                Tap navigates straight to /profile. No dropdown — the
                menu drawer lives behind the logo/page-title region
                on the left, same as desktop. */}
            <Button
              variant="ghost"
              size="icon"
              className="!rounded-md h-10 w-8 p-0"
              onClick={() => navigate('/profile')}
              data-testid="mobile-user-menu-btn"
              aria-label="View profile"
              title={user?.full_name || 'Profile'}
            >
              <Avatar key={user?.profile_picture ? 'photo' : 'no-photo'} className="!rounded-md h-9 w-7 overflow-hidden">
                {user?.profile_picture && (
                  <AvatarImage
                    src={user.profile_picture}
                    alt={user?.full_name || 'Profile'}
                    className="object-cover object-top"
                    style={{ aspectRatio: 'auto' }}
                    data-testid="mobile-avatar-image"
                  />
                )}
                <AvatarFallback className="bg-primary/10 text-primary text-sm !rounded-md">
                  {getInitials(user?.full_name)}
                </AvatarFallback>
              </Avatar>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content - Fixed padding for stationary header. The
          paddingBottom matches the global --app-bottom-h: 4rem on
          mobile + iPad portrait (clears the bottom nav), 0 on desktop
          landscape (no bottom nav → no white footer strip). When a
          mini music player is mounted we add another 4rem clearance. */}
      <main
        className="md:pt-0 pt-[calc(3.5rem+env(safe-area-inset-top,0px))]"
        style={{ paddingBottom: hasMiniPlayer ? 'calc(var(--app-bottom-h, 4rem) + 4rem)' : 'var(--app-bottom-h, 4rem)' }}
      >
        {children}
      </main>

      {/* Bottom Navigation — shown on mobile (all orientations) AND on
          iPad/tablet in PORTRAIT orientation (via @media rule in
          App.css that targets [data-layout-bottom-nav]). Hidden on
          landscape desktop/iPad where the nav lives in the top header
          instead. Also hidden when chat input is focused, and on
          full-screen pages.
          ORB embed: shown on EVERY width (desktop landscape included)
          because the top center nav is suppressed inside the iframe. */}
      {location.pathname !== '/business-card/view' && (
      <nav data-layout-bottom-nav className={`${isOrbEmbed ? '' : 'md:hidden'} fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-[#141414] border-t auto-hide-on-kb transition-transform duration-200 ${chatInputFocused ? 'translate-y-full' : 'translate-y-0'}`} style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="flex justify-around items-center h-16 px-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const isLocked = needsOnboarding && item.requiresOnboarding;
            
            // Check if feature is disabled via admin toggles
            const isFeatureDisabled = item.featureId && !isFeatureEnabled(item.featureId);
            const shouldHide = isFeatureDisabled;
            
            // Hide completely if feature is disabled
            if (shouldHide) return null;
            
            return (
              <Link
                key={item.path}
                to={isLocked ? '#' : item.path}
                onClick={(e) => handleLockedTabClick(e, item)}
                className={`flex flex-col items-center justify-center gap-1 py-2 px-3 rounded-lg transition-colors relative ${
                  isActive 
                    ? 'text-primary bg-primary/10' 
                    : 'text-muted-foreground hover:text-foreground'
                } ${isLocked ? 'opacity-50' : ''}`}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                <div className="relative">
                  <Icon className={`w-5 h-5 ${isLocked ? 'blur-[1px]' : ''}`} />
                  {isLocked && (
                    <Lock className="w-3 h-3 absolute -top-1 -right-2 text-muted-foreground" />
                  )}
                </div>
                <span className={`text-[10px] font-medium ${isLocked ? 'blur-[1px]' : ''}`}>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
      )}
      {/* Legacy per-feature 'Feature Locked' overlay removed in
       *  iter 96 — replaced by route-level <RequireOnboarded> in
       *  App.js, which renders <OnboardingLockedOverlay> with the
       *  full black background + 'Return to Onboarding' CTA. */}
    </div>
  );
};

export default Layout;
